import type { FC, ReactNode } from 'react'
import type {
    Door,
    DoorColor,
    DoorStyle,
    QuoteItem
} from '@/types'

import { useState } from 'react'
import Image from 'next/image'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRange } from '@/lib/format'
import { useQuoteList } from '@/components/generals/quote-list-provider'
import { doorSwatches } from '@/constants/doors'

const formDoorType: Record<DoorStyle, string> = {
    Traditional: "Traditional raised panel",
    'Carriage house': "Carriage house",
    Modern: "Modern / flush",
    'Full-view glass': "Full-view glass"
}

export const doorImageFor = (door: Door, color: DoorColor): string =>
    door.colorImages?.[color] ?? door.image

export const doorQuoteItem = (door: Door, color: DoorColor): QuoteItem => ({
    key: `door:${door.id}:${color}`,
    kind: 'door',
    title: door.name,
    detail: `${color} · ${door.style} · Insulation ${door.rValue}`,
    priceLabel: formatRange(door.priceFrom, door.priceTo),
    image: doorImageFor(door, color),
    formService: 'new-door',
    answers: { door_type: formDoorType[door.style] }
})

interface DoorCardProps {
    door: Door
    preferredColor?: DoorColor | null
    className?: string
    sizes?: string
}

export const DoorCard: FC<DoorCardProps> = ({
    door,
    preferredColor,
    className,
    sizes = '(min-width: 1280px) 30vw, (min-width: 768px) 45vw, 90vw'
}): ReactNode => {
    const startColor = preferredColor && door.colors.includes(preferredColor) ? preferredColor : door.colors[0]
    const [color, setColor] = useState<DoorColor>(startColor)
    const startSource = doorImageFor(door, startColor)
    const activeSource = doorImageFor(door, color)
    const [sources, setSources] = useState<string[]>([startSource])
    const [loaded, setLoaded] = useState<string[]>([])
    const { has, toggle } = useQuoteList()
    const item = doorQuoteItem(door, color)
    const added = has(item.key)

    const chooseColor = (option: DoorColor) => {
        const source = doorImageFor(door, option)

        setColor(option)
        setSources(current => current.includes(source) ? current : [...current, source])
    }

    const markLoaded = (source: string) => {
        setLoaded(current => current.includes(source) ? current : [...current, source])
    }

    return (
        <article className={cn('group flex flex-col overflow-hidden rounded-[26px] border border-ink/[0.07] bg-card transition-shadow duration-500 hover:shadow-[0_30px_60px_-36px_rgba(21,23,26,0.45)]', className)}>
            <div className='relative aspect-[4/3] overflow-hidden bg-sand'>
                <div className='absolute inset-0 transition-transform duration-[1400ms] ease-expo group-hover:scale-[1.06]'>
                    {sources.map(source => {
                        const active = source === activeSource

                        return (
                            <Image
                                key={source}
                                src={source}
                                alt={active ? `${door.name} garage door in ${color}` : ''}
                                aria-hidden={active ? undefined : true}
                                fill
                                sizes={sizes}
                                onLoad={() => markLoaded(source)}
                                className={cn(
                                    'object-cover transition-opacity duration-500 ease-out',
                                    source === startSource || (active && loaded.includes(source)) ? 'opacity-100' : 'opacity-0',
                                    active ? 'z-[1]' : 'z-0'
                                )}
                            />
                        )
                    })}
                </div>

                <span className='absolute left-4 top-4 rounded-full bg-cream/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink backdrop-blur-md'>
                    {door.style} · {door.material}
                </span>
            </div>

            <div className='flex flex-1 flex-col p-5 sm:p-6'>
                <div className='flex items-start justify-between gap-4'>
                    <h3 className='font-heading text-[1.4rem] font-semibold leading-tight tracking-[-0.03em]'>
                        {door.name}
                    </h3>

                    <p className='shrink-0 pt-1 font-mono text-[13px] text-ink'>
                        {formatRange(door.priceFrom, door.priceTo)}
                    </p>
                </div>

                <div className='mt-4 flex items-center justify-between gap-4'>
                    <div role='radiogroup' aria-label={`Color options for ${door.name}`} className='flex items-center gap-2'>
                        {door.colors.map(option => (
                            <button
                                key={option}
                                type='button'
                                role='radio'
                                aria-checked={option === color}
                                aria-label={option}
                                title={option}
                                onClick={() => chooseColor(option)}
                                className={cn(
                                    'size-6 rounded-full ring-offset-2 ring-offset-card transition-[box-shadow,transform,translate,scale,rotate] duration-300 hover:scale-110',
                                    option === color ? 'ring-2 ring-ink' : 'ring-1 ring-ink/20'
                                )}
                                style={{ backgroundColor: doorSwatches[option] }}
                            />
                        ))}
                    </div>

                    <p className='text-[13.5px] text-slate'>
                        {color} · Insulation {door.rValue}
                    </p>
                </div>

                <p className='mt-1 text-[12.5px] text-slate/80'>
                    Price covers installation and taking your old door away
                </p>

                <button
                    type='button'
                    onClick={() => toggle(item)}
                    aria-pressed={added}
                    aria-label={added ? `Remove ${door.name} in ${color} from quote list` : `Add ${door.name} in ${color} to quote list`}
                    className={cn(
                        'mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-full text-sm font-medium transition-colors duration-300',
                        added ? 'bg-ink text-cream hover:bg-graphite' : 'border border-ink/15 text-ink hover:border-ink hover:bg-ink hover:text-cream'
                    )}
                >
                    {added ? <Check className='size-4' aria-hidden='true' /> : <Plus className='size-4' aria-hidden='true' />}
                    {added ? "Saved to your quote" : "Save to your quote"}
                </button>
            </div>
        </article>
    )
}
